/**
 * Shared column definitions and formula evaluation for Quotation and Invoice item tables.
 * Supports dynamic columns: add, rename, and formula-based columns (e.g. [qty]*[unitPrice]).
 */

export type ColumnType = "readonly" | "text" | "number" | "select" | "formula";

export interface ColumnDef {
  id: string;
  label: string;
  type: ColumnType;
  /** For select: option values */
  options?: string[];
  /** For formula: expression using [columnId] references, e.g. "[qty]*[unitPrice]" */
  formula?: string;
}

/**
 * Resolve [label or id] in formula to a numeric value from row.
 * If columns are provided, matches by column label (case-insensitive) or id; otherwise by id only.
 */
export function evaluateFormula(
  formula: string,
  row: Record<string, number | string | undefined>,
  columns?: ColumnDef[],
): number {
  if (!formula || !formula.trim()) return 0;
  const expr = formula.replace(/\[([^\]]+)\]/g, (_, key) => {
    const k = key.trim();
    let id: string | undefined = k;
    if (columns?.length) {
      const byId = row[k];
      if (byId !== undefined && byId !== "") id = k;
      else {
        const byLabel = columns.find(
          (c) => c.label.toLowerCase() === k.toLowerCase(),
        );
        if (byLabel) id = byLabel.id;
      }
    }
    const v = id !== undefined ? row[id] : undefined;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? String(n) : "0";
  });
  if (!/^[\d\s+\-*/().]+$/.test(expr)) return 0;
  try {
    const result = new Function("return " + expr)();
    const value = Number.isFinite(result) ? result : 0;
    return Math.round(value * 100) / 100;
  } catch {
    return 0;
  }
}

/**
 * Get the formula column used for line total (for subtotal/total calculation).
 * Prefers the column with id "total" or "amount"; otherwise uses the last formula column.
 * This stays stable when columns are reordered.
 */
export function getLineTotalFormulaColumn(columns: ColumnDef[]): ColumnDef | undefined {
  const formulaCols = columns.filter((c) => c.type === "formula" && c.formula);
  if (!formulaCols.length) return undefined;
  return (
    formulaCols.find((c) => c.id === "total" || c.id === "amount") ??
    formulaCols[formulaCols.length - 1]
  );
}

/** Columns that can be used in formulas (numeric or formula type). */
export function getFormulaEligibleColumns(columns: ColumnDef[]): ColumnDef[] {
  return columns.filter(
    (c) =>
      c.type === "number" ||
      (c.type === "formula" && c.formula) ||
      c.id === "qty" ||
      c.id === "unitPrice",
  );
}

/**
 * Normalize formula to use column ids instead of labels.
 * Store this result so formulas keep working after column renames.
 */
export function normalizeFormulaToIds(
  formula: string,
  columns: ColumnDef[],
): string {
  if (!formula || !columns.length) return formula;
  return formula.replace(/\[([^\]]+)\]/g, (_, key) => {
    const k = key.trim();
    const byId = columns.find((c) => c.id === k);
    if (byId) return `[${k}]`;
    const byLabel = columns.find(
      (c) => c.label.toLowerCase() === k.toLowerCase(),
    );
    return byLabel ? `[${byLabel.id}]` : `[${k}]`;
  });
}

/**
 * Convert formula from stored ids to labels for display.
 * Use when showing formula in the UI so users see friendly names.
 */
export function formulaToDisplay(
  formula: string,
  columns: ColumnDef[],
): string {
  if (!formula || !columns.length) return formula;
  return formula.replace(/\[([^\]]+)\]/g, (_, key) => {
    const k = key.trim();
    const col = columns.find((c) => c.id === k);
    return col ? `[${col.label}]` : `[${k}]`;
  });
}

/** Default columns for Quotation items table */
export function getDefaultQuotationColumns(): ColumnDef[] {
  return [
    { id: "sno", label: "#", type: "readonly" },
    { id: "description", label: "Description", type: "text" },
    { id: "unit", label: "Unit", type: "text" },
    { id: "qty", label: "Qty", type: "number" },
    { id: "unitPrice", label: "Unit Price", type: "number" },
    { id: "discountType", label: "Discount Type", type: "select", options: ["None", "Fixed", "Percentage"] },
    { id: "discount", label: "Discount", type: "number" },
    { id: "total", label: "Total", type: "formula", formula: "[qty] * [unitPrice] - [discountAmount]" },
  ];
}

/** Default columns for Invoice items table */
export function getDefaultInvoiceColumns(): ColumnDef[] {
  return [
    { id: "sno", label: "#", type: "readonly" },
    { id: "description", label: "Description", type: "text" },
    { id: "unit", label: "Unit", type: "text" },
    { id: "qty", label: "Qty", type: "number" },
    { id: "unitPrice", label: "Unit Price", type: "number" },
    { id: "discountType", label: "Discount Type", type: "select", options: ["None", "Fixed", "Percentage"] },
    { id: "discount", label: "Discount", type: "number" },
    { id: "amount", label: "Amount", type: "formula", formula: "[qty] * [unitPrice] - [discountAmount]" },
  ];
}

/** Generate a unique column id for new columns */
export function nextColumnId(columns: ColumnDef[], prefix = "col"): string {
  const used = new Set(columns.map((c) => c.id));
  let i = 1;
  while (used.has(`${prefix}_${i}`)) i++;
  return `${prefix}_${i}`;
}

/** Default width (px) for resizable items table columns */
const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  sno: 48,
  description: 220,
  unit: 80,
  qty: 80,
  unitPrice: 100,
  discountType: 110,
  discount: 80,
  discountAmount: 100,
  total: 100,
  amount: 100,
};

/** Get default width for a column id (for resizable columns) */
export function getDefaultColumnWidth(columnId: string): number {
  return DEFAULT_COLUMN_WIDTHS[columnId] ?? 120;
}

/** Build initial column widths map from column definitions */
export function getInitialColumnWidths(columns: ColumnDef[]): Record<string, number> {
  return columns.reduce(
    (acc, col) => ({ ...acc, [col.id]: getDefaultColumnWidth(col.id) }),
    {} as Record<string, number>
  );
}
